import angular, { extend, equals } from 'angular';
import {
	Keymaps,
	LanguageExtensions, Plugins, ThemeExtensions, Validator, createEditor,
} from '@knitiv/code-editor';
import { EditorView } from '@codemirror/view';
import { Compartment } from '@codemirror/state';

/* globals Knitiv */

/**
 * @param {string} [string_]
 * @returns {any}
 */
const safeParse = string_ => {
	try {
		const string = string_ ?? 'null';
		return JSON.parse(string);
	}
	catch {
		return undefined;
	}
};

/**
 * @typedef {Object} Scope
 * @property {any} options
 * @property {boolean} disabled
 * @property {() => void} updateJsonEditor
 */

/**
 *
 * @param {import('angular').ITimeoutService} $timeout
 * @returns {any}
 */
const jsonEditorDirective = /* @ngInject */ $timeout => ({
	restrict: 'EA',
	require: 'ngModel',
	scope: {
		disabled: '=?ngDisabled',
		options: '=',
	},
	/**
	 * @param {Knitiv.Scope & Scope} scope
	 * @param {import('angular').IRootElementService} element
	 * @param {any} _
	 * @param {any} ngModel
	 */
	link(scope, element, _, ngModel) {
		/** @type {import('jsoneditor').JSONEditorOptions & {extensions: any[]}} */
		const settings = extend({}, {}, scope.options);
		element.html('');

		const validator = new Validator(settings.schema ?? {});

		let initialized = false;

		const editableSettings = new Compartment();

		const extensions = [
			ThemeExtensions.oneLight,
			LanguageExtensions.json(),
			LanguageExtensions.jsonSchema(validator),

			Plugins.FOLD_GUTTERS(),
			Plugins.MATCH_BRACKETS(),
			Plugins.AUTOCOMPLETION(),
			Plugins.CLOSE_BRACKETS(),
			Plugins.DRAW_SELECTION(),
			Plugins.DROP_CURSOR(),
			Plugins.HIGHLIGHT_ACTIVE_LINE_GUTTER(),
			Plugins.HIGHLIGHT_SELECTION_MATCHES(),
			Plugins.HIGHLIGHT_SPECIAL_CHARS(),
			Plugins.HISTORY(),
			Plugins.INDENT_ON_INPUT(),
			Plugins.MULTIPLE_CURSORS(),
			Plugins.RECTANGULAR_SELECTION(),
			Plugins.LINE_NUMBERS(),
			Plugins.KEYMAP.of(Keymaps.KNITIV_KEYMAP),
			editableSettings.of(EditorView.editable.of(!scope.disabled ?? true)),
		];

		if (settings.extensions && Array.isArray(settings.extensions) && settings.extensions.length > 0) {
			extensions.push(...settings.extensions);
		}

		/**
		 * @type {import('@knitiv/code-editor').KnitivEditor | null}
		 */
		let knitivEditor = createEditor({
			extensions,
			// @ts-ignore
			onUpdate: ({ /* value: newValue, */ editor }) => {
				if (!initialized || scope.disabled) {
					return;
				}
				const newValue = editor.view?.state.doc.toString() ?? '';
				const newContent = safeParse(newValue);
				let isValid = false;
				try {
					ngModel.$setViewValue(newContent);
					isValid = newContent !== undefined;
				}
				finally {
					// Update field validation
					ngModel.$setValidity('json', isValid);
					ngModel.$setTouched();
					$timeout(() => scope.$apply());
					// If the user specified a onChange callback, trigger it
					if (scope?.options?.onChange && typeof scope?.options?.onChange === 'function') {
						scope.options.onChange(isValid);
					}
				}
			},
		});

		scope.$watch(() => scope.disabled, () => {
			knitivEditor?.view.dispatch({
				effects: editableSettings.reconfigure(EditorView.editable.of(!scope.disabled ?? true)),
			});
		});

		element[0]?.append(knitivEditor.view?.dom);
		element.find('.cm-editor').css('height', 'inherit');

		scope.$on('$destroy', () => {
			knitivEditor = null;
		});

		scope.updateJsonEditor = () => {
			const value = angular.toJson(ngModel.$viewValue || null, true);
			knitivEditor?.setContent(value);
		};

		// eslint-disable-next-line angular/timeout-service
		setTimeout(() => {
			scope.updateJsonEditor();
			initialized = true;
		});

		scope.$watch(() => ngModel.$modelValue, (/** @type {any} */ newValue) => {
			const oldValue = safeParse(knitivEditor?.getContent());
			if (!equals(newValue, oldValue)) {
				scope.updateJsonEditor();
			}
		}, true);
	},
});

export default jsonEditorDirective;
